import React, { useCallback, useRef } from 'react'
import styled, { css } from 'styled-components'
import {
  Label3Styles,
  Body1Styles,
  Heading2Styles,
} from '../../../styles/Typography'
import { useAccessibility } from '../../../context/AccessibilityContext'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 36rem;
  margin: 0 auto;
  width: 100%;
`

const BackLink = styled.button`
  ${Label3Styles}
  background: none;
  border: none;
  padding: 0;
  margin: 0 0 2.5rem 0;
  color: rgb(var(--text-color));
  opacity: 0.6;
  cursor: pointer;
  text-align: left;
  align-self: flex-start;
  transition:
    transform 0.3s ease,
    color 0.3s ease,
    opacity 0.3s ease;

  &:hover {
    opacity: 1;
    color: rgb(var(--tltr-awesome-value));
    transform: translateX(-0.5rem);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--tltr-awesome-value));
    outline-offset: 4px;
    border-radius: 2px;
  }
`

const PanelHeading = styled.h2`
  ${Heading2Styles}
  font-family: 'Merriweather', serif;
  font-weight: 300;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1.3;
  margin: 0 0 2.5rem 0;
  color: rgb(var(--text-color));
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1.5rem;
  padding: 1.1rem 0;
  border-bottom: 1px solid rgba(var(--text-color), 0.1);
`

const RowLabel = styled.span`
  ${Body1Styles}
  font-size: clamp(1rem, 2vw, 1.15rem);
  color: rgb(var(--text-color));
  line-height: 1.3;
`

const RowControl = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-shrink: 0;
`

const textButtonBase = css`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 300;
  font-size: 1rem;
  letter-spacing: 0.05em;
  background: none;
  border: none;
  padding: 0.25rem 0.1rem;
  margin: 0;
  cursor: pointer;
  color: rgba(var(--text-color), 0.5);
  transition:
    color 0.3s ease,
    opacity 0.15s ease;

  &:hover {
    color: rgb(var(--tltr-awesome-value));
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--tltr-awesome-value));
    outline-offset: 3px;
    border-radius: 2px;
  }
`

const TextButton = styled.button`
  ${textButtonBase}
  ${({ $active }) =>
    $active &&
    css`
      color: rgb(var(--tltr-awesome-value));
    `}
`

const Separator = styled.span`
  color: rgba(var(--text-color), 0.3);
  font-family: 'Ubuntu', sans-serif;
`

const ToggleButton = styled.button`
  ${textButtonBase}
  min-width: 3.5rem;
  text-align: right;
  text-transform: lowercase;
  ${({ $active }) =>
    $active &&
    css`
      color: rgb(var(--tltr-awesome-value));
    `}
`

const ResetButton = styled.button`
  ${Label3Styles}
  background: none;
  border: none;
  padding: 0;
  margin: 2.5rem 0 0 0;
  color: rgb(var(--text-color));
  opacity: 0.5;
  cursor: pointer;
  align-self: flex-start;
  transition:
    color 0.3s ease,
    opacity 0.3s ease;

  &:hover {
    opacity: 1;
    color: rgb(var(--tltr-awesome-value));
    text-decoration: underline;
    text-underline-offset: 0.25em;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--tltr-awesome-value));
    outline-offset: 4px;
    border-radius: 2px;
  }
`

const BOOLEAN_SETTINGS = [
  { key: 'reduceMotion', label: 'Reduce animations' },
  { key: 'grayscale', label: 'Black & white' },
  { key: 'highContrast', label: 'High contrast' },
  { key: 'largerText', label: 'Larger text' },
  { key: 'underlineLinks', label: 'Always underline links' },
  { key: 'disableAmbience', label: 'Disable ambience backgrounds' },
]

const THEME_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

const ThemeRow = ({ theme, onChange }) => {
  const buttonsRef = useRef([])

  const handleKeyDown = useCallback(
    (e) => {
      const currentIndex = THEME_OPTIONS.findIndex((o) => o.value === theme)
      let nextIndex = null

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % THEME_OPTIONS.length
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        nextIndex =
          (currentIndex - 1 + THEME_OPTIONS.length) % THEME_OPTIONS.length
      } else if (e.key === 'Home') {
        nextIndex = 0
      } else if (e.key === 'End') {
        nextIndex = THEME_OPTIONS.length - 1
      }

      if (nextIndex === null) return
      e.preventDefault()
      const next = THEME_OPTIONS[nextIndex]
      onChange(next.value)
      buttonsRef.current[nextIndex]?.focus()
    },
    [theme, onChange]
  )

  return (
    <Row>
      <RowLabel id="a11y-theme-label">Theme</RowLabel>
      <RowControl role="radiogroup" aria-labelledby="a11y-theme-label">
        {THEME_OPTIONS.map((option, index) => {
          const active = theme === option.value
          return (
            <React.Fragment key={option.value}>
              {index > 0 && <Separator aria-hidden="true">·</Separator>}
              <TextButton
                ref={(el) => (buttonsRef.current[index] = el)}
                type="button"
                role="radio"
                aria-checked={active}
                tabIndex={active ? 0 : -1}
                $active={active}
                onClick={() => onChange(option.value)}
                onKeyDown={handleKeyDown}
              >
                {option.label}
              </TextButton>
            </React.Fragment>
          )
        })}
      </RowControl>
    </Row>
  )
}

const BooleanRow = ({ label, on, onToggle }) => (
  <Row>
    <RowLabel>{label}</RowLabel>
    <RowControl>
      <ToggleButton
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        $active={on}
        onClick={onToggle}
      >
        {on ? 'On' : 'Off'}
      </ToggleButton>
    </RowControl>
  </Row>
)

const AccessibilityPanel = React.forwardRef(({ onBack }, ref) => {
  const { settings, updateSetting, resetSettings } = useAccessibility()

  return (
    <Container ref={ref} aria-labelledby="a11y-heading">
      <BackLink type="button" onClick={onBack} aria-label="Back to menu">
        ← Menu
      </BackLink>

      <PanelHeading id="a11y-heading">Accessibility</PanelHeading>

      <ThemeRow
        theme={settings.theme}
        onChange={(value) => updateSetting('theme', value)}
      />

      {BOOLEAN_SETTINGS.map(({ key, label }) => (
        <BooleanRow
          key={key}
          label={label}
          on={settings[key]}
          onToggle={() => updateSetting(key, !settings[key])}
        />
      ))}

      <ResetButton type="button" onClick={resetSettings}>
        Reset to defaults
      </ResetButton>
    </Container>
  )
})

AccessibilityPanel.displayName = 'AccessibilityPanel'

export default AccessibilityPanel
